"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _core = require("@linaria/core");
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _motionFunctions = require("@site/pattern-library-core/motion-functions");
var _button = _interopRequireDefault(require("@ux/button"));
var _playButton = _interopRequireDefault(require("../../elements/buttons/play-button"));
var _carouselControl = _interopRequireDefault(require("../../page-body/sections/smart-testimonials-section/carousel-control"));
var _attribution = _interopRequireDefault(require("../../page-body/sections/customer-section/attribution"));
var _helpers = require("./helpers");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const ControlsContainer = "pl-c1y5yuhp";
const ControlsWrapper = "pl-c1tyblhl";
const MobileControlsWrapper = "pl-m45ezyp";
const MobileVideoPlayButton = "pl-m1j6wvvc";
const ControlButtonWrapper = "pl-cd3ofhr";
const PlayButtonStyles = "pl-p4grq99";

/**
 * Individual control button for rotation carousel
 * @returns {React.ReactElement} Control button element
 */
function ControlButton({
  item,
  index,
  isActive,
  autoRotateInterval,
  isAutoRotating,
  baseId,
  onItemSelect,
  onKeyDown,
  buttonRef
}) {
  const hasInterval = autoRotateInterval > 0;
  const progressStyle = isActive && hasInterval ? {
    ['--progress-duration']: `${autoRotateInterval}ms`
  } : null;
  const trackingProps = _patternLibraryUtils.tracking.create(`rotation-control-${index}`, true, true);
  return /*#__PURE__*/_react.default.createElement("div", (0, _extends2.default)({
    key: index,
    className: (0, _core.cx)(ControlButtonWrapper, isActive && 'selected'),
    role: "button",
    tabIndex: 0,
    "aria-label": `Go to slide ${index + 1}`,
    "aria-pressed": isActive,
    "aria-controls": `${baseId}-content-${index}`,
    onClick: () => onItemSelect(index),
    onKeyDown: event => onKeyDown(event, index)
  }, trackingProps, {
    ref: el => {
      trackingProps.ref.current = el;
      if (buttonRef) buttonRef.current = el;
    },
    "data-cy": `rotation-control-button-${index}`,
    style: progressStyle
  }), /*#__PURE__*/_react.default.createElement(_button.default, {
    text: item?.marqueeName || `Item ${index + 1}`,
    size: "sm",
    tabIndex: -1,
    className: (0, _core.cx)(isActive && hasInterval && 'is-auto-rotating', isActive && !isAutoRotating && 'is-paused')
  }));
}
ControlButton.propTypes = {
  item: _propTypes.default.object,
  index: _propTypes.default.number,
  isActive: _propTypes.default.bool,
  autoRotateInterval: _propTypes.default.number,
  isAutoRotating: _propTypes.default.bool,
  baseId: _propTypes.default.string,
  onItemSelect: _propTypes.default.func,
  onKeyDown: _propTypes.default.func,
  buttonRef: _propTypes.default.object
};

/**
 * RotationControl Component
 *
 * Navigation controls for rotating marquee content with numbered button indicators
 * that map to different content items. Uses the established UXButton component
 * for consistency with the design system.
 *
 * @param {Object} props React props
 * @param {Array} props.items Array of content items to navigate through
 * @param {number} props.activeIndex Currently active item index
 * @param {Function} props.onItemSelect Callback function when an item is selected
 * @param {string} props.ariaLabel Accessible label for the control group
 * @param {boolean} props.disabled Whether the controls are disabled
 * @param {string} props.baseId Base ID for generating content references
 * @param {boolean} props.hasVideo Whether any items contain video content
 * @returns {React.ReactElement} RotationControl Component
 * @public
 */
// eslint-disable-next-line complexity
function RotationControl({
  items = [],
  activeIndex = 0,
  onItemSelect,
  onUserInteract,
  onCarouselNavigate,
  ariaLabel = 'Marquee navigation controls',
  disabled = false,
  baseId = 'marquee',
  isAutoRotating = false,
  autoRotateInterval = 10000,
  autoRotate = false,
  onToggleAutoRotate,
  onToggleVideo,
  isVideoPaused = false,
  isMobile = false
}) {
  const controlButtonRefs = (0, _react.useRef)([]);
  const isUserInteractionRef = (0, _react.useRef)(false);
  (0, _react.useEffect)(() => {
    if (items.length > 0 && activeIndex >= 0 && activeIndex < items.length && !isUserInteractionRef.current) {
      const currentItem = items[activeIndex];
      const itemName = currentItem?.marqueeName || `Item ${activeIndex + 1}`;
      const currentButtonElement = controlButtonRefs.current[activeIndex];
      const baseEid = currentButtonElement?.current?.getAttribute?.('data-eid');
      let autoRotationEid;
      if (baseEid) {
        autoRotationEid = baseEid.replace(`rotation_control_${activeIndex}.click`, `auto-rotation-rotation_control_${activeIndex}.impression`);
      } else {
        const trackingName = _patternLibraryUtils.tracking.applySnakeCase(`auto-rotation-rotation-control-${activeIndex}`);
        autoRotationEid = `${trackingName}.impression`;
      }
      (0, _patternLibraryUtils.logEvent)({
        eid: autoRotationEid,
        type: 'impression',
        properties: {
          slideIndex: activeIndex,
          slideName: itemName,
          totalSlides: items.length
        }
      });
    } else if (isUserInteractionRef.current) {
      isUserInteractionRef.current = false;
    }
  }, [activeIndex, items]);
  if (!items.length || items.length <= 1) {
    return null;
  }
  const handleItemSelect = index => {
    if (!disabled && index !== activeIndex) {
      isUserInteractionRef.current = true;
      if (onItemSelect) onItemSelect(index);
    }
  };
  const handlePrevButtonClick = () => {
    isUserInteractionRef.current = true;
    const previousIndex = (activeIndex - 1 + items.length) % items.length;
    const handler = onCarouselNavigate || handleItemSelect;
    handler(previousIndex);
  };
  const handleNextButtonClick = () => {
    isUserInteractionRef.current = true;
    const nextIndex = (activeIndex + 1) % items.length;
    const handler = onCarouselNavigate || handleItemSelect;
    handler(nextIndex);
  };
  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleItemSelect(index);
    }
  };
  const handleToggleClick = () => {
    if (onUserInteract) onUserInteract();
    if (onToggleAutoRotate) onToggleAutoRotate();
  };
  const handleToggleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleClick();
    }
  };
  const handleVideoToggleClick = () => {
    if (onToggleVideo) onToggleVideo();
  };
  const currentItem = items[activeIndex] || {};
  const {
    attributionHeadline,
    attributionDescription,
    headlineLockup
  } = currentItem;
  const currentItemHasVideo = (0, _helpers.hasVideoContent)(currentItem);
  const shouldShowPlayButton = currentItemHasVideo || autoRotate;
  const hasAnyVideoOrAutoRotate = items.some(item => (0, _helpers.hasVideoContent)(item)) || autoRotate;
  const hasAttribution = attributionHeadline || attributionDescription;
  const attributionPartial = (0, _helpers.getHeadlineLockupInvert)(headlineLockup) ? 'dark1' : 'light1';
  return /*#__PURE__*/_react.default.createElement("div", {
    className: ControlsContainer,
    role: "group",
    "aria-label": ariaLabel,
    "data-cy": "rotation-controls"
  }, hasAttribution && /*#__PURE__*/_react.default.createElement("div", {
    className: "attribution",
    "data-partial": attributionPartial
  }, /*#__PURE__*/_react.default.createElement(_attribution.default, {
    attributionHeadline: attributionHeadline,
    attributionDescription: attributionDescription,
    formatLegacy: () => null
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: "controls"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: ControlsWrapper
  }, items.map((item, index) => {
    if (!controlButtonRefs.current[index]) {
      controlButtonRefs.current[index] = {
        current: null
      };
    }
    return /*#__PURE__*/_react.default.createElement(ControlButton, {
      key: index,
      item: item,
      index: index,
      isActive: index === activeIndex,
      autoRotateInterval: autoRotateInterval,
      isAutoRotating: isAutoRotating,
      baseId: baseId,
      onItemSelect: handleItemSelect,
      onKeyDown: handleKeyDown,
      buttonRef: controlButtonRefs.current[index]
    });
  }), hasAnyVideoOrAutoRotate && /*#__PURE__*/_react.default.createElement("div", (0, _extends2.default)({
    className: (0, _core.cx)(ControlButtonWrapper, PlayButtonStyles, !shouldShowPlayButton && 'hidden'),
    role: "button",
    tabIndex: 0,
    "aria-label": isAutoRotating ? 'Pause auto rotation' : 'Play auto rotation',
    "aria-pressed": isAutoRotating,
    "aria-hidden": !shouldShowPlayButton,
    onClick: handleToggleClick,
    onKeyDown: handleToggleKeyDown
  }, _patternLibraryUtils.tracking.create('rotation-control-toggle', true, true), {
    "data-cy": "rotation-control-toggle"
  }), /*#__PURE__*/_react.default.createElement(_playButton.default, {
    dataCy: "rotation-control-play-button",
    size: "small",
    pause: isAutoRotating,
    handlePlay: handleToggleClick
  }))), /*#__PURE__*/_react.default.createElement("div", {
    className: MobileControlsWrapper
  }, /*#__PURE__*/_react.default.createElement("div", (0, _extends2.default)({
    className: (0, _core.cx)(MobileVideoPlayButton, !(isMobile && currentItemHasVideo) && 'hidden'),
    "aria-label": isVideoPaused ? 'Play video' : 'Pause video'
  }, _patternLibraryUtils.tracking.create('rotation-control-mobile-video-toggle', true, true), {
    "data-cy": "rotation-control-mobile-video-toggle"
  }), /*#__PURE__*/_react.default.createElement(_playButton.default, {
    dataCy: "rotation-control-mobile-video-button",
    size: "small",
    pause: !isVideoPaused,
    handlePlay: handleVideoToggleClick
  })), /*#__PURE__*/_react.default.createElement(_carouselControl.default, {
    handlePrevButtonClick: handlePrevButtonClick,
    handleNextButtonClick: handleNextButtonClick,
    prevButtonAriaLabel: "Previous slide",
    nextButtonAriaLabel: "Next slide",
    prevButtonTrackingName: "rotation-control-prev",
    nextButtonTrackingName: "rotation-control-next"
  }))));
}
RotationControl.propTypes = {
  items: _propTypes.default.array.isRequired,
  activeIndex: _propTypes.default.number,
  onItemSelect: _propTypes.default.func.isRequired,
  onUserInteract: _propTypes.default.func,
  onCarouselNavigate: _propTypes.default.func,
  ariaLabel: _propTypes.default.string,
  disabled: _propTypes.default.bool,
  baseId: _propTypes.default.string,
  isAutoRotating: _propTypes.default.bool,
  autoRotateInterval: _propTypes.default.number,
  autoRotate: _propTypes.default.bool,
  onToggleAutoRotate: _propTypes.default.func,
  onToggleVideo: _propTypes.default.func,
  isVideoPaused: _propTypes.default.bool,
  isMobile: _propTypes.default.bool,
  attributionHeadline: _propTypes.default.string,
  attributionDescription: _propTypes.default.string
};
var _default = exports.default = RotationControl;